<script setup lang="ts">
import { computed } from 'vue';
import type { CandidateInsights } from '@factoryfixinc/ats-interfaces';

import {
  type ChipCategory,
  type ChipField,
  type ChipSpec,
  buildInsightChips,
} from './candidate-insights-chips';

const props = defineProps<{
  insights: CandidateInsights;
  prioritizedFields?: ReadonlySet<ChipField>;
}>();

const chips = computed(() => buildInsightChips(props.insights, props.prioritizedFields));

// Each category renders as a flat, color-family pill: pay is green (primary), scheduling/logistics
// preferences are purple (highlight), and hiring-intent signals and languages are blue (inform).
// `conditional` keeps the category's colors but dashes the border; `default` uses them as-is.
// Full literal class strings so Tailwind's scan picks them up.
const CATEGORY_PILL: Record<ChipCategory, { default: string; conditional: string }> = {
  pay: {
    default: 'border-primary-100 bg-primary-0 text-primary-700',
    conditional: 'border-dashed border-primary-100 bg-primary-0 text-primary-700',
  },
  preference: {
    default: 'border-highlight-100 bg-highlight-0 text-highlight-700',
    conditional: 'border-dashed border-highlight-100 bg-highlight-0 text-highlight-700',
  },
  intent: {
    default: 'border-inform-100 bg-inform-0 text-inform-700',
    conditional: 'border-dashed border-inform-100 bg-inform-0 text-inform-700',
  },
  language: {
    default: 'border-inform-100 bg-inform-0 text-inform-700',
    conditional: 'border-dashed border-inform-100 bg-inform-0 text-inform-700',
  },
};

// `negative` overrides the category coloring outright: an unfavorable signal always reads
// caution-yellow, regardless of which category it belongs to.
const NEGATIVE_PILL = 'border-caution-100 bg-caution-0 text-caution-800';

function pillClass(chip: ChipSpec): string {
  switch (chip.variant) {
    case 'negative':
      return NEGATIVE_PILL;
    case 'conditional':
      return CATEGORY_PILL[chip.category].conditional;
    default:
      return CATEGORY_PILL[chip.category].default;
  }
}

// The muted suffix span (`(est.)`, `· conditional`) sits a shade lighter than the label so
// provenance never competes with the fact — one step lighter within the same family.
const CATEGORY_NOTE: Record<ChipCategory, string> = {
  pay: 'text-primary-600',
  preference: 'text-highlight-600',
  intent: 'text-inform-600',
  language: 'text-inform-600',
};

function noteClass(chip: ChipSpec): string {
  if (chip.variant === 'negative') {
    return 'text-caution-700';
  }
  return CATEGORY_NOTE[chip.category];
}
</script>

<template>
  <!-- No derivable chips ⇒ no row at all. -->
  <div
    v-if="chips.length"
    data-test-id="candidate-insights-chips"
    class="mt-3 flex flex-wrap items-center gap-1"
  >
    <!-- Tooltip is disabled when the claim carries no date, so a dateless chip has no hover card. -->
    <v-tooltip
      v-for="chip in chips"
      :key="chip.key"
      location="top"
      max-width="256px"
      content-class="insights-chip-tooltip"
      :disabled="!chip.tooltip"
    >
      <template #activator="{ props: activatorProps }">
        <!-- min-h + py (not a fixed h-6): single-line pills still measure the design's 24px,
             but a long label that wraps on a narrow card grows the pill instead of spilling
             past its border. -->
        <span
          v-bind="activatorProps"
          :data-test-id="`insight-chip-${chip.field}`"
          :tabindex="chip.tooltip ? 0 : undefined"
          class="flex min-h-6 items-center justify-center gap-1 rounded-[14px] border px-2 py-0.5 text-xs font-normal leading-[18px]"
          :class="pillClass(chip)"
        >
          <span>{{ chip.label }}</span>
          <span v-if="chip.note" class="text-[11px] font-medium" :class="noteClass(chip)">
            {{ chip.note }}
          </span>
        </span>
      </template>

      {{ chip.tooltip }}
    </v-tooltip>
  </div>
</template>
